import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { getProfile } from '../../store/auth';

import GoldSiverHeader from '../HomePage/GoldSiverHeader';

const Bank = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [depositAmount, setDepositAmount] = useState('');
    const [goldExchangeAmount, setGoldExchangeAmount] = useState('');
    const [bankData, setBankData] = useState({
        bank: { gold: 0, silver: 0 },
        hand: { gold: 0, silver: 0 }
    });
    const [loading, setLoading] = useState(true);

    const user = useSelector(state => state.auth.user_data);

    const fetchBankData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/api/bank.php');
            if (response.data.success) {
                setBankData(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch bank data', error);
            toast.error(t('Failed to load bank data'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBankData();
        dispatch(getProfile()); // Update redux state as well
    }, []);

    const handleTransaction = async (action, currency) => {
        if (!depositAmount || depositAmount <= 0) {
            toast.warning(t('Please enter a valid amount'));
            return;
        }

        try {
            const response = await axios.post('/api/api/bank.php', {
                action,
                amount: depositAmount,
                currency
            });

            if (response.data.success) {
                toast.success(t('Transaction successful'));
                setDepositAmount('');
                fetchBankData();
                dispatch(getProfile()); // Refresh user profile (hand balance)
            } else {
                toast.error(response.data.error || t('Transaction failed'));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || t('Transaction failed'));
        }
    };

    const handleExchange = async () => {
        if (!goldExchangeAmount || goldExchangeAmount <= 0) {
            toast.warning(t('Please enter a valid gold amount'));
            return;
        }

        try {
            const response = await axios.post('/api/api/bank.php', {
                action: 'exchange',
                goldAmount: goldExchangeAmount
            });

            if (response.data.success) {
                toast.success(t('Exchange successful'));
                setGoldExchangeAmount('');
                fetchBankData();
            } else {
                toast.error(response.data.error || t('Exchange failed'));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || t('Exchange failed'));
        }
    };

    const styles = {
        ar_bank_section: {
            padding: '50px 0',
            color: 'white'
        },
        ar_bank_wrapper: {
            background: 'url(/images/pokedex/bg-content.png) no-repeat scroll 0 0 / 100% 100%',
            padding: '40px',
            borderRadius: '15px',
            minHeight: '500px'
        },
        ar_bank_card: {
            background: 'rgba(0, 0, 0, 0.6)',
            border: '2px solid #ffd700',
            borderRadius: '15px',
            padding: '30px',
            textAlign: 'center',
            marginBottom: '30px',
            boxShadow: '0 0 15px rgba(255, 215, 0, 0.1)'
        },
        ar_bank_balance: {
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#ffd700',
            margin: '20px 0',
            textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
        },
        ar_bank_actions: {
            display: 'flex',
            gap: '15px',
            justifyContent: 'center',
            marginTop: '20px'
        },
        ar_bank_btn: {
            background: 'linear-gradient(to bottom, #ffd700, #b8860b)',
            border: 'none',
            color: '#000',
            padding: '10px 25px',
            borderRadius: '25px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            minWidth: '100px'
        },
        ar_bank_input: {
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid #ffd700',
            borderRadius: '5px',
            padding: '10px',
            color: 'white',
            width: '100%',
            maxWidth: '300px',
            marginBottom: '15px',
            textAlign: 'center'
        },
        ar_exchange_rate: {
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '10px',
            borderRadius: '5px',
            marginTop: '15px',
            fontSize: '14px'
        }
    };

    return (
        <div className="ar_mainBody">
            <GoldSiverHeader previous="/home" title={t('City Bank')} />

            <section style={styles.ar_bank_section}>
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            <div style={styles.ar_bank_wrapper}>
                                <div className="row justify-content-center">
                                    {/* Main Balance Card */}
                                    <div className="col-md-6">
                                        <div style={styles.ar_bank_card}>
                                            <img src="/images/mainPage/gold-coin-icon.png" alt="Gold" width="80" style={{ marginBottom: '15px' }} />
                                            <h4>{t('Your Bank Balance')}</h4>
                                            
                                            <div style={styles.ar_bank_balance}>
                                                {bankData.bank.silver.toLocaleString()} <img src="/images/icons/silver.png" alt="Silver" width="30" />
                                            </div>
                                            <div style={{fontSize: '18px', color: '#ffd700', marginBottom: '10px'}}>
                                                {bankData.bank.gold.toLocaleString()} <img src="/images/icons/gold.png" alt="Gold" width="20" />
                                            </div>

                                            <p style={{ color: '#ccc' }}>{t('Money in the bank is safe from theft and battle losses')}</p>

                                            <div style={{ marginTop: '30px' }}>
                                                <input 
                                                    type="number" 
                                                    style={styles.ar_bank_input} 
                                                    placeholder={t('Enter amount')}
                                                    value={depositAmount}
                                                    onChange={(e) => setDepositAmount(e.target.value)}
                                                />
                                                <div style={styles.ar_bank_actions}>
                                                    <button 
                                                        style={styles.ar_bank_btn} 
                                                        onClick={() => handleTransaction('deposit', 'silver')}
                                                    >
                                                        {t('Deposit Silver')}
                                                    </button>
                                                    <button 
                                                        style={{ ...styles.ar_bank_btn, background: 'linear-gradient(to bottom, #c0c0c0, #808080)' }}
                                                        onClick={() => handleTransaction('withdraw', 'silver')}
                                                    >
                                                        {t('Withdraw Silver')}
                                                    </button>
                                                </div>
                                                <div style={{...styles.ar_bank_actions, marginTop: '10px'}}>
                                                    <button 
                                                        style={{ ...styles.ar_bank_btn, fontSize: '12px', padding: '5px 15px' }} 
                                                        onClick={() => handleTransaction('deposit', 'gold')}
                                                    >
                                                        {t('Deposit Gold')}
                                                    </button>
                                                    <button 
                                                        style={{ ...styles.ar_bank_btn, background: 'linear-gradient(to bottom, #c0c0c0, #808080)', fontSize: '12px', padding: '5px 15px' }}
                                                        onClick={() => handleTransaction('withdraw', 'gold')}
                                                    >
                                                        {t('Withdraw Gold')}
                                                    </button>
                                                </div>
                                            </div>

                                            <div style={styles.ar_exchange_rate}>
                                                <i className="fa fa-info-circle"></i> {t('Daily Interest: 0.5% on bank balance')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Currency Exchange */}
                                    <div className="col-md-5">
                                        <div style={{ ...styles.ar_bank_card, borderColor: '#4a90e2', height: '95%' }}>
                                            <h4>{t('Currency Exchange')}</h4>
                                            <div className="row align-items-center" style={{ marginTop: '30px' }}>
                                                <div className="col-5">
                                                    <img src="/images/mainPage/gold-coin-icon.png" width="50" alt="Gold" />
                                                    <div style={{ marginTop: '5px' }}>Gold</div>
                                                </div>
                                                <div className="col-2">
                                                    <i className="fa fa-arrow-right" style={{ fontSize: '24px', color: '#fff' }}></i>
                                                    <i className="fa fa-arrow-left" style={{ fontSize: '24px', color: '#fff' }}></i>
                                                </div>
                                                <div className="col-5">
                                                    <img src="/images/mainPage/silver-coin-icon.png" width="50" alt="Silver" />
                                                    <div style={{ marginTop: '5px' }}>Silver</div>
                                                </div>
                                            </div>
                                            
                                            <div style={{marginTop: '20px'}}>
                                                <input 
                                                    type="number" 
                                                    style={styles.ar_bank_input} 
                                                    placeholder={t('Enter Gold Amount')}
                                                    value={goldExchangeAmount}
                                                    onChange={(e) => setGoldExchangeAmount(e.target.value)}
                                                />
                                            </div>

                                            <div style={{ ...styles.ar_exchange_rate, margin: '30px 0' }}>
                                                <strong>{t('Current Exchange Rate:')}</strong><br />
                                                1 Gold = 100 Silver
                                            </div>
                                            <button 
                                                style={{ ...styles.ar_bank_btn, background: 'linear-gradient(to bottom, #4a90e2, #0056b3)', color: 'white', width: '100%' }}
                                                onClick={handleExchange}
                                            >
                                                {t('Exchange Now')} ({t('From Bank Balance')})
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Bank;
